import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  SimpleGrid,
  Stack,
  Text,
  VStack,
  Wrap,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Body, Head, Html, Preview } from '@react-email/components';
import { render } from '@react-email/render';
import { collection, getDocs, query, updateDoc, where } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import firebaseFirestore from '~/config/firebase/firestore/firestore';
import useFormFields from '~/hooks/useFormFields';
import { useAuth } from '~/hooks/useAuth';
import { handleIsInvalidField } from '~/utils/general';
import TemplateRecurringReservationEmail from './templates/TemplateRecurringReservationEmail';
import TextAreaWithUrl from './TextAreaWithUrl';

/**
 * Plantilla del correo de las reservas que se repiten.
 *
 * De una serie sale un único correo, no uno por fecha. Por eso esta plantilla
 * no lista los campos del formulario como la de una reserva suelta: enseña el
 * resumen y manda a la página donde están todas las fechas.
 *
 * Mientras no se guarde, el servidor usa la plantilla de siempre, así que no
 * pasa nada por dejarla sin tocar.
 */

const CLAVE = 'recurring_reservation';

/** Lo que se ve la primera vez, para no empezar con la página en blanco. */
const PREDETERMINADO: FormFields = {
  headerTitle: 'Hola {{namesAndSurnames}}!',
  headerTitleEn: 'Hi {{namesAndSurnames}}!',
  headerSubtitle:
    'Tienes {{recurrenceTotal}} reservas confirmadas. Abajo está el resumen, y en el enlace puedes ver cada fecha, editarlas o cancelarlas por separado.',
  headerSubtitleEn:
    'You have {{recurrenceTotal}} confirmed bookings. Below is the summary; use the link to view, edit or cancel each date separately.',
  footerText1: 'Para ver, modificar o cancelar tus reservas <clic aquí>',
  footerText1En: 'To view, edit or cancel your bookings <click here>',
  footerUrl1: '',
  footerText2: '',
  footerText2En: '',
  footerUrl2: '',
  footerSwitch1: true,
  footerSwitch2: false,
};

type RecurringReservationProps = {
  toUser: 'client' | 'admin';
};

const RecurringReservation = ({ toUser }: RecurringReservationProps) => {
  const { user } = useAuth();
  const [, , emails] = useFormFields(user.company?.id || '', true);
  const [isLoading, setIsLoading] = useState(false);
  const [guardada, setGuardada] = useState(false);

  const {
    register,
    watch,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<FormFields>({
    mode: 'onBlur',
    resolver: yupResolver(esquema()),
    defaultValues: PREDETERMINADO,
  });

  const headerTitle = watch('headerTitle');
  const headerSubtitle = watch('headerSubtitle');
  const footerText1 = watch('footerText1');
  const footerUrl1 = watch('footerUrl1');
  const footerText2 = watch('footerText2');
  const footerUrl2 = watch('footerUrl2');

  const onHandleSubmit = async (data: FormFields) => {
    setIsLoading(true);
    try {
      const formsRef = collection(firebaseFirestore, 'forms');
      const q = query(formsRef, where('company', '==', user.company?.id || ''));
      const querySnapshot = await getDocs(q);
      if (querySnapshot.empty) return;

      const formsData = querySnapshot.docs[0].data();

      const cuerpo = (lang: 'es' | 'en') => (
        <Html>
          <Head />
          <Preview>
            {lang === 'es'
              ? `Tus reservas en ${user.company?.name ?? ''}`
              : `Your bookings at ${user.company?.name ?? ''}`}
          </Preview>
          <Body style={{ backgroundColor: '#f6f9fc', padding: '10px 0' }}>
            <TemplateRecurringReservationEmail
              lang={lang}
              logoUrl={user.company?.logoUrl ?? ''}
              headerTitle={lang === 'es' ? data.headerTitle : data.headerTitleEn}
              headerSubtitle={lang === 'es' ? data.headerSubtitle : data.headerSubtitleEn}
              footer1={separarTextoYEnlace(
                lang === 'es' ? data.footerText1 : data.footerText1En,
                data.footerUrl1
              )}
              footer2={separarTextoYEnlace(
                lang === 'es' ? data.footerText2 : data.footerText2En,
                data.footerUrl2
              )}
            />
          </Body>
        </Html>
      );

      await updateDoc(querySnapshot.docs[0].ref, {
        ...formsData,
        emails: {
          ...formsData.emails,
          [CLAVE]: {
            ...formsData.emails?.[CLAVE],
            [toUser]: {
              data,
              html: { es: render(cuerpo('es'), { pretty: true }), en: render(cuerpo('en'), { pretty: true }) },
            },
          },
        },
      });
      setGuardada(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Si ya se guardó alguna vez, se recupera lo escrito; si no, se quedan los
  // textos por defecto.
  useEffect(() => {
    const data = (emails as any)?.[CLAVE]?.[toUser]?.data;
    if (!data) return;
    (Object.keys(data) as (keyof FormFields)[]).forEach((clave) => {
      setValue(clave, data[clave]);
    });
  }, [emails, toUser, setValue]);

  return (
    <Flex flex={1} gap={4} flexDirection={'row'} width={'100%'}>
      <Flex flex={2} flexDirection={'column'}>
        <Alert status='info' fontSize='sm' borderRadius='md' mb={3}>
          <AlertIcon />
          <Box>
            De una serie de reservas sale <b>un solo correo</b>. Mientras esta plantilla no se
            guarde, se usa la de &quot;Nueva reserva&quot;.
          </Box>
        </Alert>

        <Text as='b'>Datos que puedes usar</Text>
        <Wrap mt={2} mb={2}>
          {[
            'namesAndSurnames',
            'recurrenceTotal',
            'recurrenceFechas',
            'numberPeople',
            'code',
          ].map((clave) => (
            <Text key={clave} fontSize={'sm'}>{`{{${clave}}}`}</Text>
          ))}
        </Wrap>

        <form onSubmit={handleSubmit(onHandleSubmit)}>
          <Stack mt={4} spacing={4}>
            <VStack justifyContent={'flex-start'} alignItems={'flex-start'}>
              <Text as='b'>Encabezado</Text>
              <SimpleGrid columns={2} spacing={4} width={'100%'}>
                <FormControl mb={4} isInvalid={handleIsInvalidField(errors.headerTitle?.message)}>
                  <FormLabel>Título</FormLabel>
                  <Input type='text' {...register('headerTitle')} />
                </FormControl>
                <FormControl mb={4} isInvalid={handleIsInvalidField(errors.headerTitleEn?.message)}>
                  <FormLabel>En</FormLabel>
                  <Input type='text' {...register('headerTitleEn')} />
                </FormControl>
              </SimpleGrid>
              <SimpleGrid columns={2} spacing={4} width={'100%'}>
                <FormControl mb={4} isInvalid={handleIsInvalidField(errors.headerSubtitle?.message)}>
                  <FormLabel>Subtítulo</FormLabel>
                  <Input type='text' {...register('headerSubtitle')} />
                </FormControl>
                <FormControl
                  mb={4}
                  isInvalid={handleIsInvalidField(errors.headerSubtitleEn?.message)}
                >
                  <FormLabel>En</FormLabel>
                  <Input type='text' {...register('headerSubtitleEn')} />
                </FormControl>
              </SimpleGrid>
            </VStack>

            <Box width={'100%'}>
              <Text as='b'>Pie de página</Text>
              <Text fontSize='xs' color='gray.500' mb={2}>
                En el enlace pon la dirección del formulario con el código:
                app.sontickets.com/form/{user.company?.externalId ?? 'tu-negocio'}
                ?code=&#123;&#123;code&#125;&#125;
              </Text>
              <TextAreaWithUrl
                inputUrl={{
                  onChange: (e) => setValue('footerUrl1', e.target.value),
                  value: watch('footerUrl1'),
                }}
                label='Texto 1 (el enlace a sus reservas)'
                switch={{
                  onChange: (e) => setValue('footerSwitch1', e.target.checked),
                  value: watch('footerSwitch1'),
                }}
                textArea={{
                  es: {
                    onChange: (e) => setValue('footerText1', e.target.value),
                    value: watch('footerText1'),
                  },
                  en: {
                    onChange: (e) => setValue('footerText1En', e.target.value),
                    value: watch('footerText1En'),
                  },
                }}
              />
              <TextAreaWithUrl
                inputUrl={{
                  onChange: (e) => setValue('footerUrl2', e.target.value),
                  value: watch('footerUrl2'),
                }}
                label='Texto 2 (por ejemplo, el aviso de pago)'
                switch={{
                  onChange: (e) => setValue('footerSwitch2', e.target.checked),
                  value: watch('footerSwitch2'),
                }}
                textArea={{
                  es: {
                    onChange: (e) => setValue('footerText2', e.target.value),
                    value: watch('footerText2'),
                  },
                  en: {
                    onChange: (e) => setValue('footerText2En', e.target.value),
                    value: watch('footerText2En'),
                  },
                }}
              />
            </Box>
          </Stack>

          <Button mt={8} isLoading={isLoading} loadingText='Guardando' type='submit'>
            Guardar
          </Button>
          {guardada && (
            <Text fontSize='sm' color='green.600' mt={2}>
              Guardada. A partir de ahora las series usan esta plantilla.
            </Text>
          )}
        </form>
      </Flex>

      <Flex flex={2} height={'100%'}>
        <TemplateRecurringReservationEmail
          lang='es'
          logoUrl={user.company?.logoUrl ?? ''}
          headerTitle={headerTitle}
          headerSubtitle={headerSubtitle}
          footer1={separarTextoYEnlace(footerText1, footerUrl1)}
          footer2={separarTextoYEnlace(footerText2, footerUrl2)}
        />
      </Flex>
    </Flex>
  );
};

type FormFields = {
  headerTitle: string;
  headerTitleEn: string;
  headerSubtitle: string;
  headerSubtitleEn: string;
  footerSwitch1: boolean;
  footerSwitch2: boolean;
  footerText1: string;
  footerText1En: string;
  footerText2: string;
  footerText2En: string;
  footerUrl1: string;
  footerUrl2: string;
};

const esquema = () =>
  yup.object({
    headerTitle: yup.string().required('Requerido'),
    headerTitleEn: yup.string().required('Requerido'),
    headerSubtitle: yup.string().required('Requerido'),
    headerSubtitleEn: yup.string().required('Requerido'),
    footerText1: yup.string(),
    footerText1En: yup.string(),
    footerUrl1: yup.string(),
    footerText2: yup.string(),
    footerText2En: yup.string(),
    footerUrl2: yup.string(),
    footerSwitch1: yup.boolean(),
    footerSwitch2: yup.boolean(),
  });

/** "Texto <parte enlazada>" → el texto por un lado y el enlace por otro. */
const separarTextoYEnlace = (texto?: string, url?: string) => {
  const entreAngulos = (texto ?? '').match(/<(.*)>/);
  const sinAngulos = (texto ?? '')
    .split(' ')
    .filter((palabra) => !palabra.includes('<') && !palabra.includes('>'))
    .join(' ');
  return { value: sinAngulos, url, urlTitle: entreAngulos?.[1] };
};

export default RecurringReservation;
